import { NgClass } from '@angular/common';
import { Component, inject, OnInit, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { AccountEditorData } from '@compiere-ws/models/apiz-domains';
import { CompiereDataGridType, DataStoreRequest } from '@compiere-ws/models/compiere-data-json';
import { SpecificDataService } from '@compiere-ws/services/specific-data/specific-data.service';
import { CustomDesignItemType } from '@iupics-components/models/custom-design';
import SpecificWindowUiComponent from '@iupics-components/specific/window/specific-window-ui/specific-window-ui.component';
import AutocompleteUiComponent from '@iupics-components/standard/fields/autocomplete-ui/autocomplete-ui.component';
import ButtonUiComponent from '@iupics-components/standard/fields/button-ui/button-ui.component';
import EditViewUiComponent from '@iupics-components/standard/layouts/edit-view-ui/edit-view-ui.component';
import ModalUiComponent from '@iupics-components/standard/layouts/modal-ui/modal-ui.component';
import { IupicsMessage } from '@iupics-manager/models/iupics-message';
import { DynamicContainerDirective } from '@iupics-util/directives/dynamic-container.directive';
import { TranslateModule } from '@ngx-translate/core';
import { ProgressBarModule } from 'primeng/progressbar';
import { ScrollPanelModule } from 'primeng/scrollpanel';

@Component({
  selector: 'iu-account-editor-window-ui',
  templateUrl: './account-editor-window-ui.component.html',
  styleUrls: ['./account-editor-window-ui.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    ProgressBarModule,
    ScrollPanelModule,
    DynamicContainerDirective,
    ButtonUiComponent,
    ModalUiComponent,
    TranslateModule,
  ],
  providers: [SpecificDataService],
})
export default class AccountEditorWindowUiComponent extends SpecificWindowUiComponent implements OnInit {
  private readonly specificDataService = inject(SpecificDataService);

  @ViewChild('left', { read: ViewContainerRef, static: true })
  vcrLeft: ViewContainerRef;
  @ViewChild('top', { read: ViewContainerRef, static: true })
  vcrTop: ViewContainerRef;
  @ViewChild('bot', { read: ViewContainerRef, static: true })
  @Output()
  vcrBot: ViewContainerRef;
  @ViewChild('hidden', { read: ViewContainerRef, static: true })
  vcrHidden: ViewContainerRef;
  verifAlias = false;
  verifAcc = false;
  checked = false;
  checkedNode = false;
  combination: string;
  offset: number;
  limit: number;
  hideColumns: string[] = [];
  displayColumns: string[] = [];
  firstRowSelected: number;
  accountIndex: number;
  orgIndex: number;
  lastRowSelected: any;
  hasAlias: string;
  processId = 0;

  isModal = false;

  ngOnInit(): void {
    if (this.parentComponent instanceof EditViewUiComponent) {
      if (!this.dataStore) {
        this.dataStore = this.store.newSpecificWindowData(
          this.formId,
          (this.parentComponent as EditViewUiComponent).editTabs[0].dataStored
        );
      }
    }

    let acctSchemaId =
      this.dataStore.data.C_AcctSchema_ID !== undefined && this.dataStore.data.C_AcctSchema_ID !== null
        ? this.dataStore.data.C_AcctSchema_ID.id
        : null;
    if (acctSchemaId === undefined || acctSchemaId === null) {
      const currentContext = this.getCurrentContext();
      if (currentContext.C_AcctSchema_ID !== undefined && currentContext.C_AcctSchema_ID !== null) {
        acctSchemaId = currentContext.C_AcctSchema_ID.id
          ? currentContext.C_AcctSchema_ID.id
          : currentContext.C_AcctSchema_ID;
      } else if (currentContext.$C_AcctSchema_ID !== undefined && currentContext.$C_AcctSchema_ID !== null) {
        acctSchemaId = currentContext.$C_AcctSchema_ID.id
          ? currentContext.$C_AcctSchema_ID.id
          : currentContext.$C_AcctSchema_ID;
      }
    }
    if (acctSchemaId !== undefined && acctSchemaId !== null) {
      this.subscriptions.push(
        this.specificDataService.getAccountEditorData<AccountEditorData[]>(acctSchemaId).subscribe((accountData) => {
          if (accountData && !this.verifAcc) {
            this.processAccountEditorData(accountData);
            this.verifAcc = true;
          }
        })
      );
    }
  }

  private processAccountEditorData(accountData: AccountEditorData[]): void {
    const elementData = accountData.filter((item) => item.elementType !== undefined);
    const schemaData = accountData.find((item) => item.hasAlias !== undefined);

    this.setupAliasField(schemaData);
    this.addCombinationField();
    this.setupSchemaElements(elementData);
    this.setupCreateNewCombination();
    this.finalizeSetup();
  }

  private setupAliasField(schemaData?: AccountEditorData): void {
    if (schemaData && !this.verifAlias) {
      this.hasAlias = schemaData.hasAlias || '';

      if (schemaData.hasAlias === 'Y') {
        this.addFieldToDesign('Alias', 'vcrTop', true);
      } else {
        this.addFieldToDesign('Alias', 'vcrHidden', false);
      }
      this.verifAlias = true;
    }
  }

  private addCombinationField(): void {
    this.addFieldToDesign('Combination', 'vcrTop', true);
  }

  private setupSchemaElements(elementData: AccountEditorData[]): void {
    const elementMapping = this.getElementMapping();

    for (const element of elementData) {
      const config = elementMapping[element.elementType || ''];
      if (config) {
        this.addFieldToDesign(config.columnName, 'vcrTop', true);
        if (config.setIndex) {
          config.setIndex.call(this);
        }
      } else {
        // Handle elements not in mapping
        this.addElementField(element.elementType || '');
      }
    }
  }

  private getElementMapping(): Record<string, { columnName: string; setIndex?: () => void }> {
    return {
      OO: {
        columnName: 'AD_Org_ID',
        setIndex: (): void => {
          // this.orgIndex = i + j;
          this.orgIndex = this.customDesignArray.length - 1;
        },
      },
      AC: {
        columnName: 'Account_ID',
        setIndex: (): void => {
          // this.accountIndex = i + j;
          this.accountIndex = this.customDesignArray.length - 1;
        },
      },
      U1: { columnName: 'User1_ID' },
      U2: { columnName: 'User2_ID' },
      PJ: { columnName: 'C_Project_ID' },
      OT: { columnName: 'AD_OrgTrx_ID' },
      BP: { columnName: 'C_BPartner_ID' },
      PR: { columnName: 'M_Product_ID' },
      MC: { columnName: 'C_Campaign_ID' },
      SA: { columnName: 'C_SubAcct_ID' },
      SR: { columnName: 'C_SalesRegion_ID' },
      LF: { columnName: 'C_LocFrom_ID' },
      LT: { columnName: 'C_LocTo_ID' },
      AY: { columnName: 'C_Activity_ID' },
      _0: { columnName: 'User00_ID' },
      _1: { columnName: 'User01_ID' },
      _2: { columnName: 'User02_ID' },
      _3: { columnName: 'User03_ID' },
      _4: { columnName: 'User04_ID' },
      _5: { columnName: 'User05_ID' },
      _6: { columnName: 'User06_ID' },
      _7: { columnName: 'User07_ID' },
      _8: { columnName: 'User08_ID' },
      X1: { columnName: 'User1_ID' },
      X2: { columnName: 'User2_ID' },
    };
  }

  private addElementField(elementType: string): void {
    const allElements = ['OO', 'AC', 'U1', 'U2', 'PJ', 'OT', 'BP', 'PR', 'MC', 'SA', 'SR', 'LF', 'LT', 'AY'];
    const elementMapping = this.getElementMapping();

    for (const type of allElements) {
      const config = elementMapping[type];
      if (config) {
        const isVisible = type === elementType;
        this.addFieldToDesign(config.columnName, isVisible ? 'vcrTop' : 'vcrHidden', isVisible);
        if (isVisible && config.setIndex) {
          config.setIndex.call(this);
        }
      }
    }
  }

  private addFieldToDesign(columnName: string, vcr: string, isVisible: boolean): void {
    this.customDesignArray.push({
      vcr: vcr,
      type: CustomDesignItemType.FIELD,
      columnName: columnName,
      cssClass: isVisible ? 'p-col-12 p-md-4' : undefined,
    });

    if (isVisible) {
      this.displayColumns.push(columnName);
    } else {
      this.hideColumns.push(columnName);
    }
  }

  private setupCreateNewCombination(): void {
    this.customDesignArray.push({
      vcr: 'vcrLeft',
      type: CustomDesignItemType.FIELD,
      columnName: 'CreateNewCombination',
      cssClass: 'p-col-12',
    },
    /**
     * @start_custo_code
     */
    {
      vcr: 'vcrTop', // vcrMiddle
      type: CustomDesignItemType.FIELD,
      columnName: 'Search',
      cssClass: 'p-col-12',
    },
    {
      vcr: 'vcrRight',
      type: CustomDesignItemType.FIELD,
      columnName: 'C_AcctSchema_ID',
      cssClass: 'p-col-12',
    }
    /**
     * @end_custo_code
     */
    );
  }

  private finalizeSetup(): void {
    if (this.parentComponent instanceof EditViewUiComponent) {
      this.setupCombination();
      this.setupGrid();
      this.setupInitialData();
    }
  }

  private setupCombination(): void {
    const editViewComponent = this.parentComponent as EditViewUiComponent;
    const columnSearchData =
      editViewComponent.editTabs[0].dataStored.data[this.parentComponent.container.accountEditor_ColumnSearch];

    if (columnSearchData !== undefined && columnSearchData !== null) {
      this.combination = columnSearchData.id;
    }
  }

  private setupGrid(): void {
    this.customDesignArray.push({
      vcr: 'vcrBot',
      type: CustomDesignItemType.GRID,
      tableName: 'AccountEditorWindow_table',
      cssClass: 'p-col-12',
      shouldCreateQuery: true,
      shouldSelectFirst: true,
      rowSelection: 'single',
      hiddenColumns: this.hideColumns,
    });
  }

  private setupInitialData(): void {
    let recordId: string | undefined;
    if (this.combination !== undefined && this.combination !== null) {
      recordId = `C_ValidCombination_ID,${this.combination}`;
    }
    this.synchronizeData(recordId, true);
  }

  /*addVcr(i: number) {
    let vcr: string;
    for (let k = 0; k < 5; k++) {
      if (i === 2 + k * 3) {
        vcr = 'vcrMiddle';
      }
      if (i === k * 3 && i !== 0) {
        vcr = 'vcrRight';
      }
      if (i === 4 + k * 3) {
        vcr = 'vcrLeft';
      }
    }
    return vcr;
  }*/

  selectCombination(): void {
    // permet d'annuler le refresh de l'editViewParent à la fermeture de la fenetre
    if (this.parentComponent as EditViewUiComponent) {
      this.parentComponent.isProcessLaunch = false;
    }
    this.dataStore.data.selections[0].selection =
      this.gridViews[0].GridTabInfinityScrollUiComponent.grid.api.getSelectedRows();
    const sourceFieldName = this.sourceComponent.data.columnName;
    if (sourceFieldName) {
      const event = {
        data: {
          Data_UUID: sourceFieldName + ',' + this.dataStore.data.selections[0].selection[0].C_ValidCombination_ID,
        },
      };
      (this.sourceComponent as AutocompleteUiComponent).setSearchSelectItem(event);
    }
  }

  synchronizeData(ValidCombinationId: string, start = false): void {
    if (ValidCombinationId) {
      const adWindowId = 153;
      const adTabId = 207;

      const request: DataStoreRequest = {
        windowId: adWindowId,
        record_id: ValidCombinationId,
        parent_constraint: null,
        compiereRequest: {
          windowType: CompiereDataGridType.WINDOW,
          entityId: adTabId,
          startRow: 0,
          endRow: 1,
        },
      };
      this.subscriptions.push(
        this.store.getWindowSingleData(request).subscribe((dataWs) => {
          if (dataWs !== undefined) {
            const tabOfColumns = Object.keys(dataWs.data);
            for (const value of this.displayColumns) {
              if (tabOfColumns.includes(value)) {
                if (typeof dataWs.data[value] === 'string') {
                  if (dataWs.data[value] !== null) {
                    this.dataStore.data[value] = dataWs.data[value];
                  } else {
                    this.dataStore.data[value] = null;
                  }
                }

                if (typeof dataWs.data[value] === 'object') {
                  if (dataWs.data[value] !== undefined && dataWs.data[value] !== null) {
                    if (dataWs.data[value].displayValue !== null) {
                      if (value === 'AD_Org_ID') {
                        this.dataStore.data[value] = dataWs.data[value];
                      }
                    }
                  } else {
                    this.dataStore.data[value] = null;
                  }
                }
              }
            }
          }

          if (start) {
            this.showSpecificWindow();
          }

          this.setDataContainersValueWithChangedStore();
        })
      );
    } else if (start) {
      this.showSpecificWindow();
    }
  }

  notifyFromRowSelected(selectedRows: any): void {
    if (!this.gridViews?.[0]?.GridTabInfinityScrollUiComponent?.grid?.api) {
      return;
    }

    const LengthOfRowSelected = this.gridViews[0].GridTabInfinityScrollUiComponent.grid.api.getSelectedRows().length;
    if (this.lastRowSelected !== undefined) {
      const ValidCombination = this.lastRowSelected.C_ValidCombination_ID;

      this.gridViews[0].GridTabInfinityScrollUiComponent.grid.api.forEachNode((node) => {
        if (LengthOfRowSelected === 0) {
          const data = node.getRowData();
          if (data && data.C_ValidCombination_ID === ValidCombination) {
            node.setSelected(true);
          }
        }
      });
    } else {
      const rowNode = this.gridViews[0].GridTabInfinityScrollUiComponent.grid.api.getRowAtIndex(0);
      if (rowNode) {
        const rowNodeData = rowNode.getRowData();
        this.gridViews[0].GridTabInfinityScrollUiComponent.grid.api.forEachNode((node) => {
          const nodeData = node.getRowData();
          if (LengthOfRowSelected === 0) {
            if (nodeData.C_ValidCombination_ID === rowNodeData.C_ValidCombination_ID) {
              node.setSelected(true);
            }
          }
        });
        this.lastRowSelected = rowNodeData;
        this.dataStore.data.selections[0].selection = rowNodeData;
      }
    }

    if (selectedRows.length === 1) {
      this.firstRowSelected = selectedRows[0].C_ValidCombination_ID;
      const ValidCombinationId = selectedRows[0].Data_UUID;
      this.lastRowSelected = selectedRows[0];
      this.synchronizeData(ValidCombinationId);
    } else if (selectedRows.length > 1) {
      this.gridViews[0].GridTabInfinityScrollUiComponent.grid.api.forEachNode((node) => {
        for (const selectedRow of selectedRows) {
          if (selectedRow !== undefined) {
            const nodeData = node.getRowData();
            if (
              nodeData.C_ValidCombination_ID === selectedRow.C_ValidCombination_ID &&
              nodeData.C_ValidCombination_ID !== this.firstRowSelected
            ) {
              this.gridViews[0].GridTabInfinityScrollUiComponent.grid.api.deselectAll();
              this.gridViews[0].GridTabInfinityScrollUiComponent.grid.api
                .getRowAtIndex(node.rowIndex)
                .setSelected(true);
              const selectedSingleRow = selectedRow;
              const ValidCombinationId = selectedRow.Data_UUID;
              this.firstRowSelected = selectedRow.C_ValidCombination_ID;
              this.synchronizeData(ValidCombinationId);
              this.dataStore.data.selections[0].selection.pop();
              this.dataStore.data.selections[0].selection = selectedSingleRow;
              this.lastRowSelected = selectedSingleRow;
            }
          }
        }
      });
    }
  }

  checkBeforeProcessing(): boolean {
    if (this.processId === 0) {
      const { processId } = this.fields.find(({ data }) => data.columnName === 'CreateNewCombination');
      if (processId) {
        this.processId = processId;
      }
    }
    if (this.dataStore.data.AD_Org_ID === null || this.dataStore.data.Account_ID === null) {
      this.messageManager.newMessage(
          new IupicsMessage(
              this.translateService.instant('specificWindow.fillMandatory'),
              this.fields[this.orgIndex].data.label + ' , ' + this.fields[this.accountIndex].data.label,
              'error'
          )
      );
    } else if (this.hasAlias === 'Y' && this.dataStore.data.Alias) {
      this.specificDataService
        .hasDuplicateAlias(this.dataStore.data.Alias, this.dataStore.data.AD_Client_ID.id)
        .subscribe({
          next: (hasAlias) => {
            if (hasAlias) {
              this.messageManager.newMessage(
                new IupicsMessage(this.translateService.instant('specificWindow.accountEditor.duplicateAlias'), 'error')
              );
            } else {
              this.executeProcess(this.processId);
            }
          },
        });
    } else {
      return true;
    }
  }

  notifyFromDataChange(item?: any): void {
    if (item.data.isLaunchSearchGrid) {
      // the "Combination" field is not taken into account when modifying another field
      if (item.data.columnName === 'Combination') {
        this.removeExcludedFiltersToGrid('AccountEditorWindow_table');
      } else {
        this.addExcludedFiltersToGrid('AccountEditorWindow_table', 'Combination');
      }
      this.refreshGrids(this.dataStore, false, item.data);
    }
  }
}
